<template>
  <modal
    :show.sync="showModal"
    modal-classes="modal-dialog-centered modal-xl"
  >
    <template v-slot:header>
      <h5 class="modal-title">Merge Companies</h5>
    </template>

    <div class="container-fluid align-items-center" v-loading="loading">
      <div class="row">
        <div class="col-lg-5">
          <base-input
            label="Select Company to merge from"
          >
            <el-select 
              v-model="duplicateCompany" 
              placeholder="Select Company to merge from"
              filterable
              clearable
              value-key="id"
              class="w-100"
            >
              <el-option
                v-for="company in otherCompanies"
                :key="company.id"
                :label="company.company_name"
                :value="company">
              </el-option>
            </el-select>
          </base-input>
        </div>
      </div>
      
      <div class="row" v-if="duplicateCompany">
        <div class="col-lg-5">
          <company-merge-card 
            :company="duplicateCompany" 
            title="Source Company (Will be deleted)"
            icon-class="text-warning"
          />
        </div>
        <div class="col-sm-2 text-center">
          <i class="fa fa-arrow-right arrow-right"></i>
        </div>
        <div class="col-lg-5">
          <company-merge-card 
            :company="selectedCompany" 
            title="Target Company (Will be kept)"
            icon-class="text-success"
          />
        </div>
      </div>

      <div class="row mt-4" v-if="duplicateCompany">
        <div class="col-12">
          <div class="alert alert-warning">
            <h6><i class="fa fa-exclamation-triangle mr-2"></i>Important:</h6>
            <ul class="mb-0">
              <li>All leads and events from "<strong>{{ duplicateCompany.company_name }}</strong>" will be transferred to "<strong>{{ selectedCompany.company_name }}</strong>"</li>
              <li>The source company "<strong>{{ duplicateCompany.company_name }}</strong>" will be permanently deleted</li>
              <li>Company information from the target company will be preserved</li>
              <li>This action cannot be undone</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:footer>
      <base-button
        type="secondary"
        @click="closeModal"
      >
        Cancel
      </base-button>
      <base-button
        type="danger"
        :loading="loading"
        :disabled="!duplicateCompany"
        @click="mergeCompanies"
        class="ml-2"
      >
        <i class="fa fa-exclamation-triangle mr-1"></i>
        Merge Companies
      </base-button>
    </template>
  </modal>
</template>

<script>
import { Select, Option, Notification, MessageBox } from "element-ui";
import CompanyMergeCard from "./CompanyMergeCard.vue";

export default {
  name: "CompanyMergeModal",
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    CompanyMergeCard,
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedCompany: {
      type: Object,
      required: false,
      default: () => ({})
    },
    companies: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      duplicateCompany: null,
    };
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.closeModal();
        }
      }
    },
    otherCompanies() {
      if (!this.companies.length || !this.selectedCompany || !this.selectedCompany.id) {
        return [];
      }
      return this.companies.filter((company) => company.id !== this.selectedCompany.id);
    },
  },
  methods: {
    closeModal() {
      this.duplicateCompany = null;
      this.$emit('close');
    },
    async mergeCompanies() {
      if (!this.duplicateCompany) {
        return;
      }

      // Confirm action
      const confirmed = await this.confirmMerge();
      if (!confirmed) return;

      try {
        this.loading = true;
        
        await this.$store.dispatch('companies/mergeCompanies', {
          target_company_id: this.selectedCompany.id,
          source_company_id: this.duplicateCompany.id
        });

        Notification.success({
          message: `Successfully merged "${this.duplicateCompany.company_name}" into "${this.selectedCompany.company_name}"`
        });

        this.$emit('companies-merged');
        this.closeModal();

      } catch (error) {
        console.error('Failed to merge companies:', error);
        Notification.error({
          message: 'Failed to merge companies. Please try again.'
        });
      } finally {
        this.loading = false;
      }
    },
    async confirmMerge() {
      return new Promise((resolve) => {
        MessageBox.confirm(
          `Are you sure you want to merge "${this.duplicateCompany.company_name}" into "${this.selectedCompany.company_name}"? This action cannot be undone.`,
          'Confirm Company Merge',
          {
            confirmButtonText: 'Yes, Merge',
            cancelButtonText: 'Cancel',
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
            cancelButtonClass: 'el-button--default'
          }
        ).then(() => {
          resolve(true);
        }).catch(() => {
          resolve(false);
        });
      });
    }
  }
};
</script>

<style scoped>
.arrow-right {
  font-size: 28px;
  margin-top: 50px;
}
</style>